/**
 * Validación de sucursalSeleccionada en localStorage.
 * @module utils/sucursalStorageValidation
 */

import { OBSOLETE_SUCURSAL_IDS } from './versionStoragePolicy';

const SUCURSAL_KEY = 'sucursalSeleccionada';

function safeGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeRemove(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function isSucursalRecordValid(sucursal) {
  if (!sucursal || typeof sucursal !== 'object') return false;
  if (!sucursal.id || typeof sucursal.id !== 'string') return false;
  if (OBSOLETE_SUCURSAL_IDS.has(sucursal.id)) return false;
  if (sucursal.activa === false || sucursal.visible === false) return false;
  return true;
}

export function isSucursalIdValid(sucursalId, disponibles = []) {
  const id = String(sucursalId || '').trim();
  if (!id) return false;
  if (OBSOLETE_SUCURSAL_IDS.has(id)) return false;
  if (!Array.isArray(disponibles) || disponibles.length === 0) return true;
  return disponibles.some((s) => s.id === id && isSucursalRecordValid(s));
}

/**
 * Descarta sucursal inválida y persiste la primera válida si existe.
 * @param {Array<{id:string, activa?:boolean, visible?:boolean}>} disponibles
 * @returns {{ action: 'kept'|'replaced'|'removed'|'none', sucursalId: string|null }}
 */
export function reconcileSucursalSelection(disponibles = []) {
  const guardada = safeGet(SUCURSAL_KEY);
  const validas = (disponibles || []).filter(isSucursalRecordValid);

  if (!guardada) {
    if (validas.length > 0) {
      safeSet(SUCURSAL_KEY, validas[0].id);
      return { action: 'replaced', sucursalId: validas[0].id };
    }
    return { action: 'none', sucursalId: null };
  }

  if (OBSOLETE_SUCURSAL_IDS.has(guardada)) {
    safeRemove(SUCURSAL_KEY);
    if (validas.length > 0) {
      safeSet(SUCURSAL_KEY, validas[0].id);
      return { action: 'replaced', sucursalId: validas[0].id };
    }
    return { action: 'removed', sucursalId: null };
  }

  if (validas.length > 0 && !validas.some((s) => s.id === guardada)) {
    safeRemove(SUCURSAL_KEY);
    safeSet(SUCURSAL_KEY, validas[0].id);
    return { action: 'replaced', sucursalId: validas[0].id };
  }

  return { action: 'kept', sucursalId: guardada };
}

/**
 * Limpieza temprana sin lista de sucursales (p. ej. antes de auth).
 */
export function purgeKnownInvalidSucursalSelection() {
  const guardada = safeGet(SUCURSAL_KEY);
  if (!guardada) return { action: 'none', sucursalId: null };
  if (OBSOLETE_SUCURSAL_IDS.has(guardada)) {
    safeRemove(SUCURSAL_KEY);
    return { action: 'removed', sucursalId: null };
  }
  return { action: 'kept', sucursalId: guardada };
}
